import { QRCodeSVG } from 'qrcode.react';
import './Host.css';

const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];

interface Props {
  /** Currently displayed team index (0/1/2). */
  team: number;
  /** Teams metadata — only `name` is read. */
  teams: { name: string }[];
  /** Per-team registered player names. */
  rosters: string[][];
  /** Per-team captain player names (or null). */
  captains: (string | null)[];
  /** App origin for QR URLs (e.g. https://gamy-jeopardy.sack.io). */
  origin: string;
  /** When true, renders Prev/Next/Make captain/Close controls. */
  interactive: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onJumpTo?: (i: number) => void;
  onMakeCaptain?: (name: string) => void;
  onClose?: () => void;
}

export default function InviteOverlay(props: Props) {
  const { team, teams, rosters, captains, origin, interactive } = props;
  const teamName = teams[team]?.name ?? `Team ${team + 1}`;
  const teamColor = TEAM_COLORS[team] ?? '#1565c0';
  const roster = rosters[team] ?? [];
  const captain = captains[team] ?? null;
  const total = Math.max(teams.length, rosters.length, captains.length, 3);
  const qrSize = Math.min(window.innerWidth, window.innerHeight) * 0.45;

  return (
    <div
      className="host-invite-overlay"
      onClick={interactive && props.onClose ? props.onClose : undefined}
    >
      <div className="host-invite-modal" onClick={e => e.stopPropagation()}>
        {interactive && props.onClose && (
          <button className="host-invite-close" onClick={props.onClose} aria-label="Close">×</button>
        )}

        <div className="host-invite-pager">
          {interactive && props.onPrev ? (
            <button className="host-invite-nav" onClick={props.onPrev}>‹ Prev</button>
          ) : <span style={{ width: 80 }} />}
          <div className="host-invite-team-name" style={{ color: teamColor }}>{teamName}</div>
          {interactive && props.onNext ? (
            <button className="host-invite-nav" onClick={props.onNext}>Next ›</button>
          ) : <span style={{ width: 80 }} />}
        </div>

        <div className="host-invite-instruction">Scan to join {teamName}</div>

        <div className="host-invite-qr-wrap" style={{ borderColor: teamColor }}>
          <QRCodeSVG
            value={`${origin}/buzz/${team}`}
            size={qrSize}
            bgColor="#ffffff"
            fgColor="#000000"
            level="M"
          />
        </div>
        <div className="host-invite-url">{origin}/buzz/{team}</div>

        <div className="host-invite-roster">
          <div className="host-invite-roster-title">
            {roster.length === 0 ? 'Waiting for players…' : `${roster.length} registered`}
          </div>
          {roster.map(name => (
            <div key={name} className={`host-invite-player ${name === captain ? 'is-captain' : ''}`}>
              <span className="host-invite-player-name">
                {name === captain ? '★ ' : ''}{name}
              </span>
              {interactive && props.onMakeCaptain && name !== captain && (
                <button
                  className="host-invite-make-captain"
                  onClick={() => props.onMakeCaptain!(name)}
                >
                  Make captain
                </button>
              )}
            </div>
          ))}
        </div>

        {interactive && props.onJumpTo && (
          <div className="host-invite-team-pips">
            {Array.from({ length: total }).map((_, i) => (
              <button
                key={i}
                className={`host-invite-pip ${i === team ? 'active' : ''} ${(rosters[i]?.length ?? 0) > 0 ? 'has-players' : ''}`}
                style={{ borderColor: TEAM_COLORS[i] }}
                onClick={() => props.onJumpTo!(i)}
                title={teams[i]?.name ?? `Team ${i + 1}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
